import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Plus, FileText, ClipboardCheck, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Assessments() {
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssessments();
  }, []);

  const fetchAssessments = async () => {
    try {
      const [quizzesRes, worksheetsRes] = await Promise.all([
        axios.get(`${API_URL}/api/assessments/quizzes`),
        axios.get(`${API_URL}/api/assessments/worksheets`)
      ]);
      setQuizzes(quizzesRes.data);
      setWorksheets(worksheetsRes.data);
    } catch (error) {
      toast.error('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-sky-50" data-testid="assessments-page">
      <header className="glass-header py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-slate-900 mb-2">Assessments</h2>
          <p className="text-lg text-slate-600">Create quizzes, worksheets, and manage your question bank</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <button
            onClick={() => navigate('/assessments/quiz/new')}
            className="glass-card-hover p-6 text-left group"
            data-testid="create-quiz-button"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <ClipboardCheck className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Create Quiz</h3>
            <p className="text-slate-600">Generate AI-powered quizzes</p>
          </button>

          <button
            onClick={() => navigate('/assessments/worksheet/new')}
            className="glass-card-hover p-6 text-left group"
            data-testid="create-worksheet-button"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Create Worksheet</h3>
            <p className="text-slate-600">Generate practice exercises</p>
          </button>

          <button
            onClick={() => navigate('/assessments/question-bank')}
            className="glass-card-hover p-6 text-left group"
            data-testid="question-bank-button"
          >
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
              <BookOpen className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Question Bank</h3>
            <p className="text-slate-600">Manage reusable questions</p>
          </button>
        </div>

        {/* Recent Quizzes */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">Recent Quizzes</h3>
          {loading ? (
            <div className="glass-card p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            </div>
          ) : quizzes.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <p className="text-slate-600">No quizzes yet. Create your first quiz!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {quizzes.slice(0, 6).map(quiz => (
                <button
                  key={quiz.quiz_id}
                  onClick={() => navigate(`/assessments/quiz/${quiz.quiz_id}`)}
                  className="glass-card-hover p-6 text-left"
                >
                  <div className="flex items-center justify-between mb-3">
                    <ClipboardCheck className="w-8 h-8 text-blue-600" />
                    <span className="text-sm text-slate-600">{quiz.questions.length} questions</span>
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900 mb-1">{quiz.title}</h4>
                  <p className="text-sm text-slate-600">{quiz.grade} • {quiz.subject}</p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent Worksheets */}
        <div>
          <h3 className="text-2xl font-bold text-slate-900 mb-4">Recent Worksheets</h3>
          {loading ? (
            <div className="glass-card p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            </div>
          ) : worksheets.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <p className="text-slate-600">No worksheets yet. Create your first worksheet!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {worksheets.slice(0, 6).map(worksheet => (
                <button
                  key={worksheet.worksheet_id}
                  onClick={() => navigate(`/assessments/worksheet/${worksheet.worksheet_id}`)}
                  className="glass-card-hover p-6 text-left"
                >
                  <div className="flex items-center justify-between mb-3">
                    <FileText className="w-8 h-8 text-purple-600" />
                    <span className="text-sm text-slate-600">{worksheet.exercises.length} exercises</span>
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900 mb-1">{worksheet.title}</h4>
                  <p className="text-sm text-slate-600">{worksheet.grade} • {worksheet.subject}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}